import React, { useState, useEffect, forwardRef } from 'react';
import { datasourceService } from '../services';
import {
  Checkbox,
  CustomPropertiesForm,
  FormTabs,
  Select,
  SelectOwner,
  InputStatus,
  SelectButtons,
  formatSelectOptions,
  Textarea,
  textareaInitialState,
  textareaToRaw,
} from '../components';
import { getBatchEditValue, Asset } from '../helpers';
import { useCustomProperties, useDescriptionSource, useGlobalState, useMessage } from '../hooks';

const _AddDatasetGroupForm = (
  { mode, data, submitForm, loading, setLoading, formErrors = {} },
  ref
) => {
  const initialFormState = {
    dataset_group_id: null,
    dataset_group_name: '',
    dataset_group_description: textareaInitialState({}),
    dataset_group_source_description: textareaInitialState({}),
    dataset_group_source_description_secondary: textareaInitialState({}),
    dataset_group_description_source: 'k',
    datasource_id: '',
    status_id: '',
    owner_principal_id: '',
  };

  const initialBatchEditItems = {
    batch_datasource_id: false,
    batch_owner_principal_id: false,
    batch_use_dataset_group_source_description: false,
  };

  const [loggedInUser] = useGlobalState('loggedInUser');
  const [currentDatasource] = useGlobalState('currentDatasource');
  const [statuses] = useGlobalState('statuses');
  const [formData, setFormData] = useState(initialFormState);
  const [batchEdit, setBatchEdit] = useState(false);
  const [batchEditItems, setBatchEditItems] = useState(initialBatchEditItems);
  const [datasources, setDatasources] = useState([]);
  const [defaultSelectedTab, setDefaultSelectedTab] = useState();

  const { showMessage, hideMessage } = useMessage();
  const { getDescription, getDescriptionSourceOptions } = useDescriptionSource({
    data,
    formData,
    objectName: 'dataset_group',
  });
  const {
    customProperties,
    formCustomPropertyValues,
    batchEditFormCustomPropertyValues,
    batchEditItemsFormCustomPropertyValues,
    setFormCustomPropertyValues,
    hasCustomProperties,
    getCustomPropertyValuesForSubmit,
    resetFormCustomPropertyValues,
  } = useCustomProperties({
    objectName: Asset.DatasetGroup,
    objectIds: data.map(x => x.dataset_group_id),
  });

  // When editing, use the group's own system; when creating (no `data`, or in views like User
  // where it's not on the row) fall back to the datasource currently in context.
  let system_id = data[0]?.system_id || currentDatasource?.system_id;

  useEffect(() => {
    // Get form data
    fetchDatasources();
  }, []);

  useEffect(() => {
    setBatchEdit(false);

    if (data.length === 1) {
      setFormData(prev => {
        return {
          ...prev,
          dataset_group_id: data[0].dataset_group_id,
          dataset_group_name: data[0].dataset_group_name || '',
          dataset_group_description: textareaInitialState({
            value: data[0].dataset_group_description,
          }),
          dataset_group_source_description: textareaInitialState({
            value: data[0].dataset_group_source_description,
          }),
          dataset_group_source_description_secondary: textareaInitialState({
            value: data[0].dataset_group_source_description_secondary || '',
          }),
          dataset_group_description_source:
            data[0].dataset_group_description_source || prev.dataset_group_description_source,
          use_dataset_group_source_description: data[0].use_dataset_group_source_description,
          datasource_id: data[0].datasource_id,
          status_id: data[0].status_id,
          owner_principal_id: data[0].owner_principal_id,
        };
      });
    } else if (data.length > 1 && mode !== 'add') {
      setBatchEdit(true);

      setFormData({
        datasource_id: getBatchEditValue(data, 'datasource_id'),
        status_id: getBatchEditValue(data, 'status_id'),
        owner_principal_id: getBatchEditValue(data, 'owner_principal_id'),
        dataset_group_description_source: getBatchEditValue(
          data,
          'dataset_group_description_source'
        ),
      });
    } else if (mode === 'add') {
      setFormData({
        ...formData,
        status_id: statuses.default_system_status_id,
        owner_principal_id: loggedInUser?.principal_id,
        datasource_id: currentDatasource.datasource_id,
      });
    } else {
      resetForm();
    }
  }, [mode, data, loggedInUser, currentDatasource]);

  const fetchDatasources = async () => {
    datasourceService
      .getBySystemId(system_id)
      .then(res => {
        res && setDatasources(res.datasources);
      })
      .catch(err => {
        showMessage(err, 'error');
      });
  };

  const handleInputChange = (event, formPart) => {
    let { name, value } = '';

    // Handle standard form inputs
    if (event.target !== undefined) {
      name = event.target.name;
      value = event.target.value;

      // If input element is a checkbox, we cannot use "value"
      if (event.target.type === 'checkbox') {
        value = event.target.checked;
      }

      // Handle custom form inputs
    } else {
      name = event.name;
      value = event.value;
    }

    switch (formPart) {
      case 'batch':
        setBatchEditItems({ ...batchEditItems, [name]: value });
        break;
      default:
        setFormData({ ...formData, [name]: value });
    }

    // Hide errors on input change
    if (formErrors && formErrors[name]) {
      delete formErrors[name];
      hideMessage();
    }
  };

  const handleClickOnDescription = () => {
    if (formData.dataset_group_description_source !== 'k') {
      setFormData(prevFormData => {
        return {
          ...prevFormData,
          dataset_group_description_source: 'k',
        };
      });
    }
  };

  const onSubmit = event => {
    event.preventDefault();

    setLoading(true);

    // Form validation
    //if (!formData.user_fullname || !formData.user_username) return

    let dataToSubmit;

    if (batchEdit) {
      let key = '';
      let tmp = {};
      const objects = Object.entries(batchEditItems);

      for (let i = 0; i < objects.length; i++) {
        if (objects[i][1]) {
          key = objects[i][0].replace('batch_', '');
          tmp = { ...tmp, [key]: formData[key] };
        }
      }

      dataToSubmit = data.map(item => {
        return {
          dataset_group_id: item.dataset_group_id,
          dataset_group_name: item.dataset_group_name,
          datasource_id: item.datasource_id,
          status_id: item.status_id,
          owner_principal_id: item.owner_principal_id,
          dataset_group_description_source: item.dataset_group_description_source,
          ...tmp,
          ...getCustomPropertyValuesForSubmit(item.dataset_group_id),
        };
      });
    } else {
      dataToSubmit = {
        ...formData,
        ...getCustomPropertyValuesForSubmit(),
      };

      if (dataToSubmit.dataset_group_description_source !== 'k') {
        delete dataToSubmit.dataset_group_description;
      } else {
        dataToSubmit.dataset_group_description = textareaToRaw({
          value: formData.dataset_group_description,
        });
      }

      delete dataToSubmit.dataset_group_source_description;
      delete dataToSubmit.dataset_group_source_description_secondary;
    }

    // submit form
    submitForm(dataToSubmit)
      .then(() => resetForm())
      .catch(() => {
        // Errors handled in submitForm function
      })
      .finally(() => setLoading(false));
  };

  const resetForm = () => {
    resetFormCustomPropertyValues();
    setBatchEdit(false);
    setBatchEditItems(initialBatchEditItems);
    setDefaultSelectedTab({ tab: 'general' });
    setFormData(() => {
      return {
        ...initialFormState,
        status_id: statuses.default_system_status_id,
        owner_principal_id: loggedInUser ? loggedInUser.principal_id : null,
        datasource_id: currentDatasource ? currentDatasource.datasource_id : null,
      };
    });
  };

  return (
    <div className="form">
      <form onSubmit={onSubmit} ref={ref}>
        <div className="form-block vertical">
          <InputStatus
            name="dataset_group_name"
            value={formData.dataset_group_name}
            statusValue={formData.status_id}
            assetCategory="system"
            onChange={handleInputChange}
            loading={loading}
            batchEdit={batchEdit}
            batchEditItems={batchEditItems}
            validation={
              formErrors.dataset_group_name
                ? { valid: false, message: formErrors.dataset_group_name }
                : undefined
            }
          />
        </div>

        <FormTabs
          defaultSelectedTab={defaultSelectedTab}
          formErrors={formErrors}
          hideTabsIfOnlyOne={true}
        >
          <div label="General">
            {!batchEdit && (
              <div className="form-block vertical">
                <Textarea
                  name="dataset_group_description"
                  value={getDescription()}
                  onChange={handleInputChange}
                  onClick={handleClickOnDescription}
                  disabled={loading || formData.dataset_group_description_source !== 'k'}
                  title={
                    formData.dataset_group_description_source !== 'k'
                      ? `The description is automatically synced from the datasource`
                      : ''
                  }
                  isOptional={true}
                />
              </div>
            )}
            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_dataset_group_description_source}
                    name="batch_dataset_group_description_source"
                    label="Edit Description Source"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}

              <SelectButtons
                name="dataset_group_description_source"
                value={
                  formData.dataset_group_description_source === 'multiple'
                    ? undefined
                    : formData.dataset_group_description_source
                }
                options={getDescriptionSourceOptions()}
                onChange={handleInputChange}
                disabled={
                  loading || (batchEdit && !batchEditItems.batch_dataset_group_description_source)
                }
                title="Select whether to use a custom description set here in Katalogue or automatically sync the description from the datasource."
              />
            </div>

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_datasource_id}
                    name="batch_datasource_id"
                    label="Edit Datasource"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}
              <Select
                name="datasource_id"
                label={!batchEdit}
                value={formData.datasource_id === 'multiple' ? undefined : formData.datasource_id}
                options={formatSelectOptions({
                  options: datasources,
                  optionValue: 'datasource_id',
                  optionLabel: 'datasource_name',
                  optionIcon: 'datasource_type_category',
                  tooltip: 'datasource_description',
                })}
                onChange={handleInputChange}
                placeholder={formData.datasource_id === 'multiple' ? '< Multiple >' : ''}
                disabled={loading || (batchEdit && !batchEditItems.batch_datasource_id)}
              />
            </div>

            <div className="form-block vertical">
              {batchEdit && (
                <label>
                  <Checkbox
                    checked={batchEditItems.batch_owner_principal_id}
                    name="batch_owner_principal_id"
                    label="Edit Owner"
                    onChange={e => handleInputChange(e, 'batch')}
                    disabled={loading}
                  />
                </label>
              )}

              <SelectOwner
                name="owner_principal_id"
                value={
                  formData.owner_principal_id === 'multiple'
                    ? undefined
                    : formData.owner_principal_id
                }
                label={!batchEdit}
                placeholder={formData.owner_principal_id === 'multiple' ? '< Multiple >' : ''}
                onChange={handleInputChange}
                disabled={loading || (batchEdit && !batchEditItems.batch_owner_principal_id)}
              />
            </div>
          </div>

          {hasCustomProperties && (
            <div label="More">
              <CustomPropertiesForm
                customProperties={customProperties}
                formData={formCustomPropertyValues}
                batchEdit={batchEditFormCustomPropertyValues}
                batchEditItems={batchEditItemsFormCustomPropertyValues}
                onChange={setFormCustomPropertyValues}
                loading={loading}
              />
            </div>
          )}
        </FormTabs>
      </form>
    </div>
  );
};

export const AddDatasetGroupForm = forwardRef(_AddDatasetGroupForm);
